import { useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'
import Tippy from '@tippyjs/react'
import Footer from '@app/components/Footer'
import Navbar from '@app/components/Navbar'
import Label from '@app/components/Label'
import TextField from '@app/components/TextField/TextField'
import Dialog from '@app/components/Dialog/Dialog'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import Button from '@app/components/Button'
import { useFormik } from 'formik'
import { QuizStep, updateQuiz } from '@app/store/slices/quiz'
import Checkbox from '@app/components/Checkbox'
import programs from '@app/programs'
import SelectField from '@app/components/SelectField'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useParams } from 'react-router-dom'
import useCampaignHref from '@app/hooks/useCampaignHref'
import CustomLink from '@app/components/CustomLink'
import S from './styles'
import QuizHeader from './components/QuizHeader/QuizHeader'
import Valuation from './components/Valuation/Valuation'
import useQuizRedirect from './useQuizRedirect'
import RedirectPartialView from './RedirectPartialView'
import { getValueByPurchasedProduct, getValueByTradedProduct } from './utils'

const TradeInDetails = () => {
  const params = useParams<{ program: string; campaign?: string }>()
  const dispatch = useAppDispatch()
  const quiz = useAppSelector(state => state.quiz)
  const { messages } = useIntl()
  const [showExtraFields, setShowExtraFields] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const { formatMessage } = useIntl()
  const { href: termsUrl } = useCampaignHref({ requestPath: 'terms' })

  const { isRedirecting } = useQuizRedirect({ requestedStep: QuizStep.VALUATION })

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      manufacturer: quiz.tradeInManufacturer,
      serialNumber: quiz.tradeInSerial,
      isWorking: quiz.tradeInWorking,
    },
    validationSchema: yup.object({
      manufacturer: yup.string().required(messages['static.form.error_select_required'] as string),
      serialNumber: yup.string().when('manufacturer', {
        is: (val: string) => val === 'Dyson',
        then: schema =>
          schema
            .min(15, messages['static.form.error_serial_length'] as string)
            .max(15, messages['static.form.error_serial_length'] as string),
        otherwise: schema => schema.notRequired(),
      }),
      isWorking: yup.boolean().oneOf([true], messages['static.form.error_device_condition'] as string),
    }),
    onSubmit: values => {
      dispatch(
        updateQuiz({
          tradeInManufacturer: values.manufacturer,
          tradeInSerial: values.serialNumber,
          tradeInWorking: values.isWorking,
          step: QuizStep.ADDRESS,
          prevStep: QuizStep.VALUATION,
        })
      )
      setShowDialog(true)
    },
  })

  /**
   * Determines the amount to be paid for the trade-in based on promotions offer rules
   */
  const amount = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { offer_strategy } = programs[params.program!]

    if (offer_strategy === 'combined_sku') {
      return getValueByPurchasedProduct(params.program!, quiz.purchaseSerial, quiz.tradeInManufacturer)
    }

    if (offer_strategy === 'traded_sku') {
      return getValueByTradedProduct(params.program!, quiz.tradeInSerial, quiz.tradeInManufacturer)
    }

    return 0
  }, [params.program, quiz.purchaseSerial, quiz.tradeInManufacturer, quiz.tradeInSerial])

  useEffect(() => {
    if (formik.values.manufacturer === 'Dyson') {
      setShowExtraFields(true)
    } else {
      setShowExtraFields(false)
    }
  }, [formik.values.manufacturer])

  if (isRedirecting) {
    return <RedirectPartialView />
  }

  return (
    <>
      <Navbar />
      <S.QuizMain>
        <QuizHeader
          title={<FormattedMessage id="quiz_traded_product_page.content.base.title" />}
          intro={<FormattedMessage id="quiz_traded_product_page.content.base.intro" />}
          nextStep={<FormattedMessage id="quiz_traded_product_page.content.base.button_text" />}
          step={2}
          progress={28}
        />
        <S.Form onSubmit={formik.handleSubmit}>
          <S.FieldGroup>
            <SelectField
              name="manufacturer"
              label={
                messages['quiz_traded_product_page.content.questions.traded_product_manufacturer.question'] as string
              }
              placeholder={messages['static.form.placeholder_select'] as string}
              options={programs[params.program!].traded_products.map(r => ({ value: r.value, label: r.value }))}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                Boolean(formik.errors.manufacturer) && Boolean(formik.touched.manufacturer)
                  ? formik.errors.manufacturer
                  : undefined
              }
            />
          </S.FieldGroup>
          {showExtraFields && (
            <TextField
              data-testid="serialNumber"
              name="serialNumber"
              label={messages['static.form.label_tradein_serial'] as string}
              hint={messages['static.form.hint_tradein_serial'] as string}
              placeholder="000-00-00000000"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              mask={{ blocks: [3, 2, 8], delimiter: '-', uppercase: true }}
            />
          )}
          <S.FieldGroup>
            <Label>
              <FormattedMessage id="quiz_traded_product_page.content.questions.agree_to_terms.question" />
            </Label>
            <Checkbox
              id="terms"
              name="isWorking"
              value="test"
              checked={formik.values.isWorking}
              onChange={formik.handleChange}>
              <S.Label>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: CustomLink }}>
                  {
                    formatMessage(
                      { id: 'quiz_traded_product_page.content.questions.agree_to_terms.checkbox_label' },
                      { terms_url: termsUrl }
                    ) as string
                  }
                </ReactMarkdown>
              </S.Label>
            </Checkbox>
            <S.TermsList>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {messages['quiz_traded_product_page.content.questions.agree_to_terms.terms_summary'] as string}
              </ReactMarkdown>
            </S.TermsList>
          </S.FieldGroup>
          <S.ActionBar>
            <Tippy
              content={<FormattedMessage id="static.form.hint_continue" />}
              placement="bottom"
              disabled={formik.isValid}>
              <div>
                <Button type="submit" disabled={!formik.isValid}>
                  <FormattedMessage id="quiz_traded_product_page.content.base.button_text" />
                </Button>
              </div>
            </Tippy>
          </S.ActionBar>
        </S.Form>
      </S.QuizMain>
      <Footer />
      <Dialog maxWidth={44} show={showDialog} onExit={() => setShowDialog(false)}>
        <Valuation value={amount} />
      </Dialog>
    </>
  )
}

export default TradeInDetails
